/**
 * 이상한 방 - 세션 효과 번역의 순수한 조각 (자막 · 발견 연출 인자 · 소리 이름)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(잔탄 경고) · 4.4절(원래 방 쿨타임) · 4.5절(점수)
 *            `이상한방-상태모델-v0.1.md` 11.3절(클라이언트가 해도 되는 것)
 *            로드맵 M2(`sessionEffects` - `session.ts`에서 순수한 것만 뺀다)
 *
 * `session.ts`에서 갈라져 나왔다. 여기 있는 것은 전부 **인자만 보는 순수 함수**이고, 하는 일은 리듀서의 효과
 * 하나를 화면 모듈이 아는 모양(자막 키 · 연출 인자 · 소리 이름)으로 옮기는 **번역**이다. 세션 클로저(HUD 핸들 ·
 * 씬 · 시계)를 하나도 붙잡지 않으므로 렌더러 없이 그대로 부를 수 있고, 두 번째 세션(`matchSession.ts`)이
 * 같은 효과 모양을 받으면 같은 함수를 부른다 - 자막 문구·연출 인자가 두 벌이 되지 않는다.
 *
 * ## 무엇이 남고 무엇이 왔는가
 *
 * `phaseEnter`·`warpTo`의 배선은 세션마다 다르다(전환 이펙트의 소유권 · 자막 시계 잠금 · 리빌 하드컷) - 그건
 * 여기 오지 않는다. 온 것은 셋뿐이다:
 *
 *  - `noticeForEffect`  효과 → 토스트 한 줄의 **키와 인자**. 문장은 만들지 않는다(`t()`는 부르는 쪽이) -
 *                        i18n은 전역 locale을 읽는 모듈이라 순수 함수가 부를 것이 아니고, 매치 호스트가
 *                        같은 효과를 내면 문장 조립은 클라이언트의 몫이다(로드맵 6절 "서버의 i18n 문장 합성" 기각)
 *  - `hitJuiceArgs`     콤보 수(+ 걸기 여부) → 발견 연출 인자. 배수·상한·점수는 전부 규칙 모듈(`combo`·`hunt`)에서
 *                        그대로 옮긴다. 여기서 하나라도 다시 계산하면 자막의 "+240"과 총점이 갈린다
 *  - `raisedSoundName`  걸기(M7) 성립의 소리 이름. 두 세션이 같은 이름을 여기서 받는다
 *  - `soundNameFor`     리듀서의 `SoundId` → 사전의 `SoundName`. 표에 없는 id는 `undefined`다(조용히 넘긴다 -
 *                        규칙 쪽에 소리가 하나 늘면 그 소리만 나지 않고 게임은 그대로 돈다)
 *  - `toShotResult`     탭 판정 결과(`PickResult`) → 사격 대상의 최소 형태(`ShotResultLike`). 두 세션의 탭이 같은
 *                        번역을 지난다 - 한 벌만 고치면 빈 표면(`empty`)이 한쪽에서만 관대해진다(M2에서 옮겨 왔다)
 *  - `huntStampDelayMs` 수색 자막까지 기다릴 시간 = 화면을 덮고 있는 연출의 실제 길이. 전환 상수의 합이라 두 세션에
 *                        사본이 있으면 `TRANSITION_TIMING_MS` 하나가 바뀐 날 한쪽 자막만 전환 끝과 어긋난다(M2 리뷰)
 *
 * 라운드 끝소리(`round-over`)는 **여기서 고르지 않는다.** 실패·퍼펙트를 가르는 데 필요한 정보(발견 수 · 목표
 * 달성)는 세션이 들고 있고, 그 판단은 `sessionResult.metItsGoal`이 하나로 쥔다(`session.roundOverSound`).
 *
 * ## `ui/`·`audio/`의 타입을 가져오는 이유
 *
 * 세션은 접착층이라 위 레이어를 전부 부른다(CLAUDE.md의 의도된 예외). 이 파일은 그 세션의 한 조각이라 같은
 * 예외 안에 있고, 실제로 가져오는 것은 **뷰 타입과 소리 표**뿐이다. `boundary.test.ts`가 `session*`을 금지
 * 목록에 두므로 매치 리듀서·네트 층이 이 파일을 부를 길은 없다 - 그래야 한다. 여기 있는 것은 연출이다.
 */

import { SOUND_FOR_GAME_ID, type SoundName } from '../audio/sounds'
import { ceilSeconds, MS_PER_SECOND } from '../core/time'
import type { MessageKey } from '../i18n/ko'
import type { PickResult } from '../input/tapPicker'
import { TRANSITION_TIMING_MS } from '../render/transition'
import type { NoticeTone } from '../ui/gameHud'
import type { HitJuice } from '../ui/juice'
import { comboMultiplier, comboPitchStep, isComboAtCap } from './combo'
import type { Effect, SoundId } from './gameState'
import { findPointsForEntry, type ShotResultLike } from './hunt'

/* --- 자막 ------------------------------------------------------------------------ */

/**
 * 토스트 한 줄의 재료. 문장이 아니라 **키와 인자**다 - `t(key, params)`는 세션이 부른다.
 * `params`가 없으면 키만 있는 문장이다(`hud.ammoLow`).
 */
export type Notice = Readonly<{
  key: MessageKey
  params?: Readonly<Record<string, string | number>>
  tone: NoticeTone
}>

/**
 * 효과 하나가 토스트 한 줄을 요구하는가. 요구하지 않으면 `null`이다.
 *
 * 네 효과만 말한다. `foundChange`의 "N개 남았다"(마지막 하나는 "전부 찾았다"), `lowAmmo`의 경고,
 * `originEntryDenied`의 "N초 뒤", `raised`의 "걸었다"(M7 - 칩이 두 겹이 되는 것은 뷰가 말하고, 이 한 줄은 그 순간을
 * 찍는 시점 표시다). 나머지 효과는 자막이 아니라 다른 연출(워프 · 소리 · 프록시)이거나
 * 화면이 이미 말하는 것(`magazineUsed` - HUD가 `+N`을 스스로 띄운다. `session.ts`의 그 주석을 보라)이다.
 *
 * 쿨타임 초는 **올림**이다. 1.2초 남았을 때 "1초 뒤"라고 하면 1초 뒤에도 못 들어간다 - HUD가 거짓말을 하지 않는다.
 */
export function noticeForEffect(effect: Effect): Notice | null {
  switch (effect.type) {
    case 'foundChange':
      return effect.remaining > 0
        ? { key: 'notice.remaining', params: { count: effect.remaining }, tone: 'good' }
        : { key: 'result.end.all-found', tone: 'good' }
    case 'lowAmmo':
      return { key: 'hud.ammoLow', tone: 'warn' }
    case 'originEntryDenied':
      return {
        key: 'hud.origin.cooldown.label',
        params: { seconds: ceilSeconds(effect.remainingMs) },
        tone: 'warn',
      }
    case 'raised':
      return { key: 'raise.notice.self', tone: 'good' }
    case 'phaseEnter':
    case 'warpTo':
    case 'syncProxies':
    case 'playSound':
    case 'comboUp':
    case 'comboBroken':
    case 'magazineUsed':
      return null
  }
}

/* --- 발견 연출 -------------------------------------------------------------------- */

/**
 * 발견 한 건의 연출 인자. 콤보 수(와 걸기 여부)에서 넷을 편다 - 음정 단계 · 배수 · 상한 · 점수.
 *
 * 넷 전부 규칙 모듈의 함수 **그대로**다. 점수는 `hunt.findPointsForEntry` 하나로 계산한다 - 결과 내역(`computeScoreBreakdown`)과
 * **같은 함수**여야 자막의 "+240"과 총점이 맞아떨어지고, 걸기(M7) 뒤의 "+480"도 같은 이유로 여기서 `× 2`를 적지 않는다.
 * 인자로 받는 콤보·걸기는 리듀서가 방금 올려 둔 값(`comboUp`이 `foundChange`보다 먼저 나온다 · `raised`는 그 발견 기록에
 * 굳은 것과 같은 값)이고, 여기서 다시 세면 규칙이 두 벌이 된다. 배수 칩(`multiplier`)은 콤보의 것 그대로다 - 걸기의 ×2는
 * HUD의 걸기 칩이 두 겹으로 이미 말하고 있어 자막에 한 번 더 적으면 같은 사건이 두 곳에서 말해진다.
 */
export function hitJuiceArgs(combo: number, raised = false): HitJuice {
  return {
    comboStep: comboPitchStep(combo),
    multiplier: comboMultiplier(combo),
    atCap: isComboAtCap(combo),
    score: findPointsForEntry({ combo, raised }),
  }
}

/* --- 소리 ------------------------------------------------------------------------ */

/**
 * 리듀서의 소리 요청을 사전의 이름으로. 표에 없으면 `undefined` - 던지지 않는다(머리말).
 *
 * `round-over`도 표의 기본값(`roundClear`)을 그대로 돌려준다. 실패·퍼펙트로 바꾸는 것은 세션의 판단이고
 * (`session.roundOverSound`), 그 판단에 필요한 정보는 이 함수에 없다.
 */
export function soundNameFor(id: SoundId): SoundName | undefined {
  return SOUND_FOR_GAME_ID[id]
}

/**
 * 걸기(M7)가 성립한 순간의 소리. `SoundId`를 새로 두지 않고(리듀서 diff를 최소로 두려는 M7의 결정) 페이즈 전환음을 다시 쓴다 -
 * "판이 바뀌었다"는 뜻이 같아서다. 싱글 세션과 여럿이 효과 번역기가 **이 함수 하나**를 부른다 - 이름을 두 곳에 적어 두면
 * 한쪽만 다른 소리로 바뀌는 날이 온다(CLAUDE.md "소리 이름은 sessionEffects의 순수 함수 하나를 함께 부른다").
 */
export function raisedSoundName(): SoundName {
  return 'phaseChange'
}

/* --- 자막 시각 --------------------------------------------------------------------- */

/**
 * 수색 자막을 띄우기까지 기다릴 시간(ms). **화면을 덮고 있는 연출의 실제 길이**다.
 *
 * 숫자를 적지 않고 두 모듈이 내보낸 상수에서 더한다 - 전환 길이를 조정하는 날 여기가 따라오지 않으면 자막이 왜곡 위에
 * 겹치거나(짧으면) 빈 화면이 남는다(길면). 이펙트가 서지 못한 판(`transitionOwnsWarp` 거짓)에서는 `warpTo`의 디졸브가
 * 대신 돌므로 그쪽 길이를 쓴다. 탭으로 전환을 건너뛴 경우 자막이 조금 늦게 뜨는데, 그 사이 시계는 잠긴 채라 손해가 없다.
 * 움직임 줄이기는 인자로 받는다 - 미디어 쿼리는 세션이 읽는다(`core/env.prefersReducedMotion`은 캐시하지 않는다).
 */
export function huntStampDelayMs(transitionOwnsWarp: boolean, warpDissolveSeconds: number, reducedMotion: boolean): number {
  if (!transitionOwnsWarp) return warpDissolveSeconds * MS_PER_SECOND
  return reducedMotion
    ? TRANSITION_TIMING_MS.reducedBlank + TRANSITION_TIMING_MS.reducedTail
    : TRANSITION_TIMING_MS.distort + TRANSITION_TIMING_MS.tail
}

/* --- 탭 ------------------------------------------------------------------------- */

/**
 * 탭 판정 결과 → 사격 판정 대상.
 *
 * `empty`(빈 표면)와 `place`(배치 전용 결과)는 **허공과 같은 취급**이다. 제거된 자리는 프록시가 `removed`로
 * 돌려주므로, 여기서 `empty`를 살려 두면 6.2절이 프록시로 만들어 준 관대한 판정을 뒷문으로 한 번 더 여는 셈이 된다.
 * 싱글(`session.shootAt`)과 여럿이(`matchSession.shootAt`)가 같은 함수를 지난다.
 */
export function toShotResult(pick: PickResult | null): ShotResultLike {
  if (pick === null) return { kind: 'miss' }

  switch (pick.kind) {
    case 'object':
      return { kind: 'object', instanceId: pick.instanceId }
    case 'removed':
      return { kind: 'removed', instanceId: pick.instanceId }
    case 'empty':
    case 'place':
      return { kind: 'miss' }
  }
}
